import { regeneratePublicIds } from 'flow/service/publicIds';
import { transformLegacyRepresentationToJson } from 'flow/store/engine/transformation';
import type { WorkflowFailureHandlingMode, WorkflowProps } from 'flow/store/engine/types';
import { getMatrixModels } from '~/flow/matrix-models/services';
import type { FlowState } from '~/flow/types';
import apiCalls from '~/helpers/apiCalls';

interface GetLocalSnippetWorkflowTransformedData {
  snippetToken: string;
  typhonDomain: string;
  versionId: number;
  workflowProps: WorkflowProps;
  preservePublicIds?: boolean;
}

export default async function getLocalSnippetWorkflowTransformedData({
  snippetToken,
  typhonDomain,
  versionId,
  workflowProps,
  preservePublicIds = false,
}: GetLocalSnippetWorkflowTransformedData) {
  const workflowData: FlowState = await apiCalls.workflows.getWorkflowVersion({
    applicationToken: snippetToken,
    publicVersion: versionId,
  });

  const matrixModels = await getMatrixModels(typhonDomain, workflowData);

  const publishedAttributes = workflowProps.publishedAttributes.map(pa => ({
    ...pa,
    isEphemeral: false,
  }));

  const transformedData = transformLegacyRepresentationToJson({
    actions: workflowData.actions,
    actionOptions: workflowData.actionOptions,
    thresholds: workflowData.thresholds,
    dataRoutes: workflowData.dataRoutes,
    tags: workflowProps.tags,
    services: workflowProps.services,
    outcomes: workflowProps.outcomes,
    publishedAttributes,
    matrixModels,
    lists: workflowProps.lists,
    customLists: workflowProps.customLists,
    reasonCodeGroups: workflowProps.reasonCodeGroups,
    snippets: workflowProps.snippets,
  });


  const remappedGraphData = preservePublicIds
    ? {
      actions: transformedData.actions,
      thresholds: transformedData.thresholds,
      dataRoutes: transformedData.dataRoutes,
    }
    : regeneratePublicIds({
      actions: transformedData.actions,
      thresholds: transformedData.thresholds,
      dataRoutes: transformedData.dataRoutes,
    });


  return {
    transformedData: {
      ...transformedData,
      actions: remappedGraphData.actions,
      thresholds: remappedGraphData.thresholds,
      dataRoutes: remappedGraphData.dataRoutes,
      failureHandlingMode: workflowData.version?.use_best_effort_threshold_rule_parser
        ? 'best-effort'
        : 'full-threshold' as WorkflowFailureHandlingMode,
      sdkConfig: workflowData.sdkConfig,
    },
    extraResponseData: {
      publishedAttributes,
    },
  };
}
